// Fila de burbujas de la tarjeta: primero las promos de venta y luego las de crédito del equipo.
// Si no cabe se desliza sola; se detiene con hover o al tocarla y respeta prefers-reduced-motion.
import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { cn } from '@/lib/cn';
import {
    AUTO_SCROLL_START,
    cutEdges,
    snappedMax,
    stepAutoScroll,
    type AutoScrollState,
} from '../autoScroll';
import type { BenefitBubble } from '../promotionSlots';
import { COUPON_CHIP } from '../promotionConstants';

/** Tras soltar la fila en pantalla táctil, cuánto sigue quieta. */
const TOUCH_RESUME_MS = 4_000;
const FADE_PX = 18;

const BUBBLE_TONE: Record<NonNullable<BenefitBubble['tone']> | 'sale', string> = {
    sale: 'bg-success-soft text-success',
    coupon: COUPON_CHIP,
    credit: 'bg-promo-soft text-promo',
    gift: 'bg-info-soft text-info',
    benefit: 'bg-warning-soft text-warning',
};

export function Bubble({ bubble }: { bubble: BenefitBubble }) {
    const Icon = bubble.icon;
    return (
        <span
            title={bubble.title ?? bubble.label}
            className={cn(
                'inline-flex h-6 max-w-full flex-none items-center gap-1 whitespace-nowrap rounded-full px-2.5 text-[11px] font-bold tabular-nums',
                BUBBLE_TONE[bubble.tone ?? 'sale'],
            )}
        >
            <Icon className="size-3 flex-none" aria-hidden />
            {/* Una burbuja más ancha que la tarjeta se trunca: en reposo siempre se ve completa. */}
            <span className="min-w-0 truncate">{bubble.label}</span>
        </span>
    );
}

function prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function fadeMask(edges: { start: boolean; end: boolean }): CSSProperties | undefined {
    if (!edges.start && !edges.end) return undefined;
    const from = edges.start ? `transparent, black ${String(FADE_PX)}px` : 'black, black';
    const to = edges.end ? `black calc(100% - ${String(FADE_PX)}px), transparent` : 'black';
    const mask = `linear-gradient(to right, ${from}, ${to})`;
    return { maskImage: mask, WebkitMaskImage: mask };
}

/** Cajas de las burbujas (sin el relleno del final), relativas a la fila. */
function bubbleBoxes(el: HTMLElement): { left: number; width: number }[] {
    return (Array.from(el.children) as HTMLElement[])
        .filter((child) => child.dataset.spacer === undefined)
        .map((child) => ({ left: child.offsetLeft, width: child.offsetWidth }));
}

/** Relleno al final para que el descanso del final caiga al inicio de una burbuja. */
function fitSpacer(el: HTMLElement, spacer: HTMLElement | null): void {
    if (!spacer) return;
    const boxes = bubbleBoxes(el);
    const lastBox = boxes.at(-1);
    if (!lastBox) return;
    const contentEnd = lastBox.left + lastBox.width;
    const target = snappedMax(
        boxes.map((b) => b.left),
        contentEnd - el.clientWidth,
    );
    spacer.style.width = `${String(Math.max(0, target + el.clientWidth - contentEnd))}px`;
}

/** Lleva la fila al inicio de la burbuja más cercana, para que en pausa ninguna quede a la mitad. */
function snapToBubble(el: HTMLElement): void {
    const max = el.scrollWidth - el.clientWidth;
    const starts = bubbleBoxes(el)
        .map((b) => b.left)
        .filter((left) => left <= max + 1);
    if (starts.length === 0) return;
    const left = starts.reduce((best, start) =>
        Math.abs(start - el.scrollLeft) < Math.abs(best - el.scrollLeft) ? start : best,
    );
    if (typeof el.scrollTo === 'function') el.scrollTo({ left, behavior: 'smooth' });
    else el.scrollLeft = left;
}

/** Desliza la fila mientras no quepa y no esté en pausa; devuelve qué orillas desvanecer.
 * Con otras burbujas vuelve a empezar desde la primera. */
function useAutoScroll(
    ref: RefObject<HTMLSpanElement | null>,
    spacerRef: RefObject<HTMLSpanElement | null>,
    paused: RefObject<boolean>,
    contentKey: string,
) {
    const [edges, setEdges] = useState({ start: false, end: false });

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const reduced = prefersReducedMotion();
        el.scrollLeft = 0;
        let state: AutoScrollState = AUTO_SCROLL_START;
        let frame = 0;
        let last = 0;

        const syncEdges = (): void => {
            const next = cutEdges(bubbleBoxes(el), el.scrollLeft, el.clientWidth);
            setEdges((prev) => (prev.start === next.start && prev.end === next.end ? prev : next));
        };
        const tick = (now: number): void => {
            const elapsed = last === 0 ? 0 : now - last;
            last = now;
            const max = el.scrollWidth - el.clientWidth;
            if (max <= 0) {
                frame = 0;
                last = 0;
                return;
            }
            if (paused.current) state = { ...state, offset: el.scrollLeft };
            else {
                state = stepAutoScroll(state, max, elapsed);
                el.scrollLeft = state.offset;
            }
            frame = requestAnimationFrame(tick);
        };
        const start = (): void => {
            fitSpacer(el, spacerRef.current);
            syncEdges();
            if (reduced || frame !== 0 || el.scrollWidth <= el.clientWidth) return;
            frame = requestAnimationFrame(tick);
        };

        start();
        el.addEventListener('scroll', syncEdges, { passive: true });
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(start);
        observer?.observe(el);
        return () => {
            cancelAnimationFrame(frame);
            el.removeEventListener('scroll', syncEdges);
            observer?.disconnect();
        };
    }, [ref, spacerRef, paused, contentKey]);

    return edges;
}

export function BenefitRow({ bubbles }: { bubbles: readonly BenefitBubble[] }) {
    const rowRef = useRef<HTMLSpanElement>(null);
    const spacerRef = useRef<HTMLSpanElement>(null);
    const paused = useRef(false);
    const resumeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const contentKey = bubbles.map((b) => `${b.key}:${b.label}`).join('|');
    const edges = useAutoScroll(rowRef, spacerRef, paused, contentKey);
    useEffect(
        () => () => {
            clearTimeout(resumeTimer.current);
        },
        [],
    );

    const pause = (): void => {
        clearTimeout(resumeTimer.current);
        paused.current = true;
        if (rowRef.current) snapToBubble(rowRef.current);
    };
    const resume = (delayMs = 0): void => {
        clearTimeout(resumeTimer.current);
        resumeTimer.current = setTimeout(() => {
            paused.current = false;
        }, delayMs);
    };

    return (
        <span
            ref={rowRef}
            data-testid="benefit-row"
            style={fadeMask(edges)}
            onPointerEnter={(e) => {
                if (e.pointerType === 'mouse') pause();
            }}
            onPointerLeave={(e) => {
                if (e.pointerType === 'mouse') resume();
            }}
            onPointerDown={(e) => {
                if (e.pointerType !== 'mouse') pause();
            }}
            onPointerUp={(e) => {
                if (e.pointerType !== 'mouse') resume(TOUCH_RESUME_MS);
            }}
            className="relative flex h-full flex-nowrap items-center gap-1 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
            {bubbles.map((b) => (
                <Bubble key={b.key} bubble={b} />
            ))}
            {/* -ml-1 anula el gap: el relleno solo alarga lo justo para alinear el final. */}
            <span ref={spacerRef} data-spacer aria-hidden className="-ml-1 block h-px flex-none" />
        </span>
    );
}
